// Skip-link prefactor (US-255 — Wave 11 a11y pass).
//
// Prefix anchor that lets keyboard users jump past the chrome straight to the
// main content. The DS `AppShell` already renders an internal skip link, but
// US-255 demands an explicit, theme-aware, translatable surface mounted by
// the app — so we expose one here. The shell injects the same `<main>` id
// (`main-content`) the AppShell defaults to, so a single link covers both
// the AppShell-only and AppShell-less surfaces (e.g., onboarding wizard
// override) once Wave 11 lights it up.

import type { CSSProperties } from 'react';

const SKIP_TARGET_ID = 'main-content';

const SKIP_LINK_STYLE: CSSProperties = {
  position: 'absolute',
  top: 'var(--space-2)',
  left: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  background: 'var(--bg-elevated)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  fontWeight: 600,
  zIndex: 'var(--z-modal)' as unknown as number,
  textDecoration: 'none',
};

export interface SkipLinkProps {
  /** Pre-localised label (defaults to a fallback string for resilience). */
  label: string;
  /** Anchor target id. Defaults to the AppShell main landmark. */
  targetId?: string;
}

/**
 * SkipLink — visually hidden until focused, then renders a high-contrast pill
 * in the top-left corner. Activates the `<main>` landmark with native anchor
 * semantics so JAWS/NVDA announce the destination.
 */
export function SkipLink({ label, targetId = SKIP_TARGET_ID }: SkipLinkProps) {
  return (
    <a
      href={`#${targetId}`}
      data-region="skip-link"
      className="ls-focus-ring sr-only focus:not-sr-only"
      style={SKIP_LINK_STYLE}
    >
      {label}
    </a>
  );
}

export const APP_MAIN_LANDMARK_ID = SKIP_TARGET_ID;
